import {
    ChangeDetectionStrategy,
    Component,
    ComponentFactory,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { Sort, SortDirection } from "@angular/material/sort";
import { ClareSort, TableActionDisplay, TableActionOptions, TableColumnDef, TableFilterProperties } from "@app/shared/model/action.model";
import { ExcludeFunctions, NgChanges } from "@app/shared/model/ng-changes.model";

import { PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { SearchPayload } from "../clare-search/clare-search.component";

/**
 * @description The configuration for a dynamic component that is displayed
 * @interface ComponentFactoryConfig
 */
export interface ComponentFactoryConfig {
    factory: ComponentFactory<any>;
    config: Record<string, any>;
}

@Component({
    selector: "clare-custom-table",
    templateUrl: "./clare-custom-table.component.html",
    styleUrls: ["./clare-custom-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClareCustomTableComponent<T extends { id: string }> implements OnInit, OnChanges {
    @Input() title: string;
    @Input() columns: Record<string, string | TableColumnDef>;
    @Input() source: T[][];
    @Input() isLoading = true;
    @Input() isMobileView: boolean;
    @Input() tableActionOptions: TableActionOptions<T>;
    @Input() selectedRow: ExcludeFunctions<Record<string, any> & { id: string }>;
    @Input() dataSourceLength: number;
    @Input() isSearchSuffixIconShown = false;

    // Optional
    @Input() tableHint = "";
    @Input() shouldHidePagination: boolean;
    @Input() shouldHideRefresh = false;
    @Input() shouldHideSearch = false;

    @Input() pageSize: number;
    @Input() sortActive: string;
    @Input() sortDirection: SortDirection;
    @Input() pageIndex: number;
    @Input() hidePageSize = false;
    @Input() isFilterHidden = false;
    @Input() placeholder = "";
    @Input() tableClass = "";
    @Input() initialQueryString = "";
    @Input() shouldShowBlankCellDash = false;

    @Output() editHandler = new EventEmitter<string>();
    @Output() selectHandler = new EventEmitter<T>();
    @Output() sortChangeHandler = new EventEmitter<ClareSort>();
    @Output() pageChangeHandler = new EventEmitter<PageEvent>();
    @Output() refreshDataHandler = new EventEmitter<void>();
    @Output() changeFilterHandler = new EventEmitter<Partial<TableFilterProperties>>();

    @Input() factoryConfig: ComponentFactoryConfig;
    @Input() exportCSVFactoryConfig: ComponentFactoryConfig;

    @ViewChild("exportcsv", { static: true, read: ViewContainerRef }) exportCSVContainerRef: ViewContainerRef;
    @ViewChild("container", { static: false, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;

    public pageSizeOptions: number[] = [5, 10, 20, 50, 100, 200];
    public columnsToDisplay: string[];
    public tableData = new MatTableDataSource<T>();
    public spinnerText: string;
    /**
     * @description Determines if the table actions should be displayed separately or in a drop list.
     */
    isSeparatedAction = false;

    isDynamicBarAction = false;

    ngOnInit() {
        this.spinnerText = "Loading " + this.title;

        if (this.source) {
            this.tableData.data = this.source[this.pageIndex];
        }
    }

    /**
     * @description Calls page event to bubble up the page event to the parent component when a mat-pagination event occurs
     * @param {PageEvent} $event
     */
    pageEvent($event: PageEvent) {
        this.pageChange($event);
    }

    /**
     * @description Emits an event to a parent component when a sort event occurs in the mat-sort component
     * @param {Sort} $event the sort event
     */
    sortEvent($event: Sort) {
        const selectedCol = this.columns[$event.active];
        if (selectedCol && typeof selectedCol === "object") {
            this.sortChangeHandler.emit({ ...$event, dataSortType: selectedCol.dataSortType });
        } else {
            this.sortChangeHandler.emit({ ...$event, dataSortType: "default" });
        }
    }

    /**
     * @description Prevents the row from going to edit page when checkbox is clicked
     * @param {Event} $event the browser click event
     */
    preventClick($event: Event) {
        $event?.stopPropagation();
    }

    /**
     * @description Emits an event to a parent component to select or deselect a row item when it is clicked.
     * @param {*} row the row to select or deselect
     */
    selectRow(row: T) {
        this.selectHandler.emit(this.selectedRow?.id !== row?.id ? row : undefined);
    }

    /**
     * @description Checks if the id is the selected row or not
     * @param {string} id the id to check
     * @returns {boolean} is the id the selected row?
     */
    isRowSelected(id: string): boolean {
        return this.selectedRow?.id === id;
    }

    /**
     * @description Tracks items in the table by id for better performance
     * @param {number} _index the index of the row item
     * @param {string} item the item
     * @returns {string} the item id
     */
    trackById(_index: number, item: T): string {
        return item?.id;
    }

    /**
     * @description Tracks items in the table by name for better performance
     * @param {number} _index the index of the row item
     * @param {string} item the item
     * @returns {string} the item
     */
    trackByName(_index: number, item: string): string {
        return item;
    }

    /**
     * @description Emits an event when a row item is clicked
     * @param {string} id the id of the row item
     */
    edit(id: string) {
        this.editHandler.emit(id);
    }

    ngOnChanges(changes: NgChanges<ClareCustomTableComponent<T>>) {
        if (changes.tableActionOptions?.currentValue) {
            this.isDynamicBarAction = changes.tableActionOptions.currentValue.display === TableActionDisplay.dynamicBar;
            this.isSeparatedAction =
                changes.tableActionOptions.currentValue.display === TableActionDisplay.separated || this.isDynamicBarAction;
        }
        if (changes.source?.currentValue) {
            this.tableData.data = changes.source.currentValue[0];
            this.pageIndex = 0;
        }

        if (changes.columns?.currentValue) {
            let displayColumns = changes.columns.currentValue;
            if (this.isSeparatedAction) {
                displayColumns = {
                    select: "",
                    ...displayColumns,
                };
            } else if (this.tableActionOptions?.display && this.tableActionOptions?.display !== TableActionDisplay.noaction) {
                displayColumns = {
                    ...displayColumns,
                    action: "",
                };
            }

            this.columnsToDisplay = Object.keys(displayColumns);
        }

        if (changes.selectedRow?.currentValue) {
            this.selectedRow = changes.selectedRow.currentValue;
        }

        if (changes.factoryConfig?.currentValue) {
            this.initViewChild(changes.factoryConfig.currentValue, this.viewContainerRef);
        }

        if (changes.exportCSVFactoryConfig?.currentValue) {
            this.initViewChild(changes.exportCSVFactoryConfig.currentValue, this.exportCSVContainerRef);
        }

        if (changes.pageSize && !this.pageSizeOptions.find(size => size === changes.pageSize.currentValue)) {
            this.pageSizeOptions.push(changes.pageSize.currentValue);
            this.pageSizeOptions.sort((a, b) => (a < b ? -1 : 1));
        }
    }

    /**
     * @description Emits the prepared search term to the parent component
     * @param {SearchPayload} searchTerm the term to search in object form
     */
    public onDataSearch(searchTerm: SearchPayload) {
        this.changeFilterHandler.emit(searchTerm);
    }

    /**
     * @description Emits an event when the refresh button is pressed
     */
    public onDataRefresh() {
        this.refreshDataHandler.emit();
    }
    /**
     * @description Dynamically places a component in the table element.
     * TODO: Should use content projection here, if possible.
     * @private
     * @param {ComponentFactoryConfig} factoryConfig the factory config for the component being created
     * @param {ViewContainerRef} viewContainerRef the view container ref to create the component in
     */
    private initViewChild(factoryConfig: ComponentFactoryConfig, viewContainerRef: ViewContainerRef) {
        if (factoryConfig?.factory && viewContainerRef) {
            const componentRef = viewContainerRef.createComponent<any>(factoryConfig.factory);
            componentRef.instance.config = factoryConfig?.config;
            componentRef.changeDetectorRef.detectChanges();
        }
    }

    /**
     * @description Emits a page change event when a pagination element is clicked
     * @param {PageEvent} $event the page event to bubble up
     */
    public pageChange($event: PageEvent) {
        if ($event.pageSize !== this.pageSize) {
            this.pageSize = $event.pageSize;
            this.pageIndex = 0;
            this.pageChangeHandler.emit($event);
        } else if ($event.pageIndex !== this.pageIndex) {
            this.pageIndex = $event.pageIndex;
            this.tableData.data = this.source[this.pageIndex];
            this.selectHandler.emit();
        }
    }
}
